import { useEffect, useMemo, useRef, useState } from 'react'
import { plan_top_down_update_channel_targets_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import ChannelMatrixHead from './ChannelMatrixHead'
import ChannelMatrixRow from './ChannelMatrixRow'
import { type RequestComment } from './revisionRequest'

type Channel = { id: string; name: string }

type PlanNodeIn = {
  id: string
  parentId: string | null
  name: string
  depth: number
  hasChildren: boolean
  targetRevenue: number
}

type MatrixRow = {
  plan_node_id: string
  past: Record<string, string>
  plan: Record<string, number>
  // Actual sales for the plan's actual months. Present only on a revision.
  actual?: Record<string, string>
}

// Month labels for a revision's actual-vs-plan split; null for a plain version.
type PeriodLabels = { actual: string; actualQuarters: string; plan: string; quarters: string; months: number } | null

type Props = {
  slug: string
  /** The draft being edited; sent with every save so a stale tab (draft gone) gets refused. */
  versionId: string | null
  channels: Channel[]
  planNodes: PlanNodeIn[]
  rows: MatrixRow[]
  currency?: string | null
  // Open revision-request comments, keyed by draft plan node id.
  rowNotes?: Record<string, RequestComment[]>
  periodLabels?: PeriodLabels
  onSaveStatusChange?: (status: { saving: boolean; savedAt: Date | null }) => void
  onCompletionChange?: (next: string | null) => void
}

type CellMap = Record<string, Record<string, number>>

type DirtyKey = string // `${planNodeId}:${channelId}`

export default function ChannelMatrix({ slug, versionId, channels, planNodes, rows, currency, rowNotes, periodLabels, onSaveStatusChange, onCompletionChange }: Props) {
  const market = useMarket()
  const [cellsByNode, setCellsByNode] = useState<CellMap>(() => buildInitialCells(rows))
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [saving, setSaving] = useState(false)
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    onSaveStatusChange?.({ saving, savedAt })
  }, [saving, savedAt, onSaveStatusChange])

  const dirtyRef = useRef<Set<DirtyKey>>(new Set())
  const cellsRef = useRef<CellMap>(cellsByNode)
  const timerRef = useRef<number | null>(null)
  cellsRef.current = cellsByNode

  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, PlanNodeIn[]>()
    planNodes.forEach((n) => {
      const list = map.get(n.parentId) ?? []
      list.push(n)
      map.set(n.parentId, list)
    })
    return map
  }, [planNodes])

  const pastByNode = useMemo(() => {
    const out: Record<string, Record<string, number>> = {}
    rows.forEach((r) => {
      out[r.plan_node_id] = {}
      Object.entries(r.past).forEach(([cid, v]) => { out[r.plan_node_id][cid] = Number(v) || 0 })
    })
    return out
  }, [rows])

  const actualByNode = useMemo(() => {
    const out: Record<string, Record<string, number>> = {}
    rows.forEach((r) => {
      out[r.plan_node_id] = {}
      Object.entries(r.actual ?? {}).forEach(([cid, v]) => { out[r.plan_node_id][cid] = Number(v) || 0 })
    })
    return out
  }, [rows])

  const toggleCollapse = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const visibleNodes = useMemo<PlanNodeIn[]>(() => {
    const out: PlanNodeIn[] = []
    const walk = (parentId: string | null) => {
      const kids = childrenByParent.get(parentId) ?? []
      kids.forEach((n) => {
        out.push(n)
        if (n.hasChildren && !collapsedIds.has(n.id)) walk(n.id)
      })
    }
    walk(null)
    return out
  }, [childrenByParent, collapsedIds])

  const flush = async () => {
    if (dirtyRef.current.size === 0) return
    const keys = Array.from(dirtyRef.current)
    dirtyRef.current.clear()
    const cells = keys.map((k) => {
      const [plan_node_id, org_node_id] = k.split(':')
      return {
        plan_node_id,
        org_node_id,
        target_revenue: cellsRef.current[plan_node_id]?.[org_node_id] ?? 0
      }
    })
    setSaving(true)
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    try {
      const res = await fetch(plan_top_down_update_channel_targets_path(market.slug, slug), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ version_id: versionId, cells })
      })
      if (res.ok) {
        setSavedAt(new Date())
        const data = await res.json().catch(() => null)
        if (data && 'channel_step_completed_at' in data) {
          onCompletionChange?.(data.channel_step_completed_at)
        }
      } else {
        keys.forEach((k) => dirtyRef.current.add(k))
        const data = await res.json().catch(() => ({ error: 'Failed to save.' }))
        window.alert(data.error ?? 'Failed to save.')
      }
    } catch {
      keys.forEach((k) => dirtyRef.current.add(k))
    } finally {
      setSaving(false)
    }
  }

  const scheduleFlush = () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => { void flush() }, 500)
  }

  const onCellChange = (planNodeId: string, channelId: string, next: number) => {
    if ((cellsRef.current[planNodeId]?.[channelId] ?? 0) === next) return
    setCellsByNode((prev) => ({
      ...prev,
      [planNodeId]: { ...(prev[planNodeId] ?? {}), [channelId]: next }
    }))
    dirtyRef.current.add(`${planNodeId}:${channelId}`)
    scheduleFlush()
  }

  useEffect(() => {
    const onBeforeUnload = () => {
      if (dirtyRef.current.size === 0) return
      const keys = Array.from(dirtyRef.current)
      const cells = keys.map((k) => {
        const [plan_node_id, org_node_id] = k.split(':')
        return {
          plan_node_id,
          org_node_id,
          target_revenue: cellsRef.current[plan_node_id]?.[org_node_id] ?? 0
        }
      })
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
      const blob = new Blob([JSON.stringify({ version_id: versionId, cells, authenticity_token: csrf })], { type: 'application/json' })
      navigator.sendBeacon(plan_top_down_update_channel_targets_path(market.slug, slug), blob)
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      if (timerRef.current != null) window.clearTimeout(timerRef.current)
      void flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market.slug, slug, versionId])

  const buildRowProps = (n: PlanNodeIn) => {
    const cells: Record<string, { past: number; plan: number; actual: number }> = {}
    const past = pastByNode[n.id] ?? {}
    const actual = actualByNode[n.id] ?? {}
    channels.forEach((c) => {
      cells[c.id] = {
        past: past[c.id] ?? 0,
        plan: cellsByNode[n.id]?.[c.id] ?? 0,
        actual: actual[c.id] ?? 0,
      }
    })
    const rowSum = channels.reduce((s, c) => s + (cells[c.id]?.plan ?? 0), 0)
    return { cells, rowSum }
  }

  return (
    <div className="border border-border rounded-md">
      <div className="overflow-x-auto bg-background">
        <table className="text-xs border-collapse w-full table-auto bg-background">
          <ChannelMatrixHead channels={channels} />
          <tbody>
            {visibleNodes.map((n) => {
              const { cells, rowSum } = buildRowProps(n)
              const kids = childrenByParent.get(n.id) ?? []
              const fillableByChannel: Record<string, number | null> = {}
              const columnDeltaByChannel: Record<string, number> = {}
              channels.forEach((c) => {
                const allChildrenFilled = kids.length > 0 && kids.every((k) => (cellsByNode[k.id]?.[c.id] ?? 0) !== 0)
                if (!allChildrenFilled) {
                  fillableByChannel[c.id] = null
                } else {
                  const sum = kids.reduce((s, k) => s + (cellsByNode[k.id]?.[c.id] ?? 0), 0)
                  fillableByChannel[c.id] = sum
                }
                if (kids.length > 0) {
                  const childrenSum = kids.reduce((s, k) => s + (cellsByNode[k.id]?.[c.id] ?? 0), 0)
                  const parentVal = cellsByNode[n.id]?.[c.id] ?? 0
                  columnDeltaByChannel[c.id] = childrenSum - parentVal
                } else {
                  columnDeltaByChannel[c.id] = 0
                }
              })
              const autofillEnabled = n.hasChildren && channels.some((c) => {
                const sum = fillableByChannel[c.id]
                return sum != null && sum !== (cellsByNode[n.id]?.[c.id] ?? 0)
              })
              const handleAutofill = () => {
                channels.forEach((c) => {
                  const next = fillableByChannel[c.id]
                  if (next == null) return
                  if ((cellsByNode[n.id]?.[c.id] ?? 0) !== next) onCellChange(n.id, c.id, next)
                })
              }
              const clearEnabled = channels.some((c) => (cellsByNode[n.id]?.[c.id] ?? 0) !== 0)
              const handleClearRow = () => {
                channels.forEach((c) => {
                  if ((cellsByNode[n.id]?.[c.id] ?? 0) !== 0) onCellChange(n.id, c.id, 0)
                })
              }
              return (
                <ChannelMatrixRow
                  key={n.id}
                  label={n.name}
                  depth={n.depth}
                  channels={channels}
                  currency={currency}
                  cells={cells}
                  rowSum={rowSum}
                  rowTarget={n.targetRevenue}
                  onCellChange={(channelId, next) => onCellChange(n.id, channelId, next)}
                  hasChildren={n.hasChildren}
                  isCollapsed={collapsedIds.has(n.id)}
                  onToggleCollapse={n.hasChildren ? () => toggleCollapse(n.id) : undefined}
                  onAutofill={n.hasChildren ? handleAutofill : undefined}
                  autofillEnabled={autofillEnabled}
                  onClearRow={handleClearRow}
                  clearEnabled={clearEnabled}
                  columnDeltaByChannel={columnDeltaByChannel}
                  notes={rowNotes?.[n.id]}
                  periodLabels={periodLabels}
                />
              )
            })}
            {visibleNodes.length === 0 && (
              <tr>
                <td
                  colSpan={channels.length + 3}
                  className="px-3 py-4 text-sm text-muted-foreground text-center"
                >
                  No plan nodes yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function buildInitialCells(rows: MatrixRow[]): CellMap {
  const out: CellMap = {}
  rows.forEach((r) => { out[r.plan_node_id] = { ...r.plan } })
  return out
}
